import { Box } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import React from 'react';

import { getDimensionTypes } from '~/api/dimensions';
import { FilterSection } from '~/features/view-page/components/filter-section';
import { ShowTableOrLoad } from '~/features/view-page/components/show-table-or-load';
import { ViewPageHeader } from '~/features/view-page/components/view-page-header';
import { ViewPagePagination } from '~/features/view-page/components/view-page-pagination';
import { FilterDialog, type FilterDialogRaw } from '~/features/view-page/filter-dialog';
import { type Data, DataService } from '~/services/data-service';
import { DimensionsService } from '~/services/dimensions';
import type { DimensionType } from '~/types/api-types';

export type FilterName =
  | 'Version'
  | 'CRF'
  | 'NFR'
  | 'Category'
  | 'Classification'
  | 'Secret'
  | 'Undergrupp'
  | 'Measure'
  | 'Gas'
  | 'Unit'
  | 'Source'
  | 'Method'
  | 'Target'
  | 'Option'
  | 'Type'
  | 'UID';

export interface Filter {
  id: number;
  name: FilterName;
  value: string;
}

interface ViewPageState {
  visibleEmissions: string[];
  visibleDimensions: FilterName[];
  appliedFilters: Filter[];
  data: Data[];
  dimensionTypes: DimensionType[];
  timeDimensionId: number | null;
  hiddenRows: number;
  currentPage: number;
  dataPerPage: number;
  visibleData: Data[];
  loadingData: boolean;
  nbrOfPages: number;
  exporting: boolean;
  exportError: string;
  activeAbortController: AbortController | null;
}

class ViewPage extends React.Component<{}, ViewPageState> {
  public static readonly FILTER_DIMENSION_COLUMNS_KEY = 'FILTER_DIMENSION_COLUMNS';
  public static readonly FILTER_EMISSION_COLUMNS_KEY = 'FILTER_EMISSION_COLUMNS';

  private static readonly DEFAULT_DIMENSION_COLUMNS: FilterName[] = [
    'Version',
    'CRF',
    'NFR',
    'Category',
    'Classification',
    'Undergrupp',
    'Measure',
    'Secret',
  ];
  private static readonly DEFAULT_EMISSION_COLUMNS: string[] = [
    '2010',
    '2011',
    '2012',
    '2013',
    '2014',
    '2015',
    '2016',
    '2017',
    '2018',
    '2019',
    '2020',
    '2021',
    '2022',
    '2023',
    '2024',
    '2025',
    '2026',
    '2027',
    '2028',
    '2029',
    '2030',
    '2031',
    '2032',
    '2033',
  ];

  private readonly dialogRef: React.RefObject<FilterDialogRaw>;

  constructor(props: ViewPageState) {
    super(props);
    this.state = {
      visibleEmissions: JSON.parse(
        localStorage.getItem(ViewPage.FILTER_EMISSION_COLUMNS_KEY) || JSON.stringify(ViewPage.DEFAULT_EMISSION_COLUMNS)
      ),
      visibleDimensions: JSON.parse(
        localStorage.getItem(ViewPage.FILTER_DIMENSION_COLUMNS_KEY) ||
          JSON.stringify(ViewPage.DEFAULT_DIMENSION_COLUMNS)
      ),
      appliedFilters: [],
      data: [],
      dimensionTypes: [],
      timeDimensionId: null,
      hiddenRows: 0,
      currentPage: 1,
      dataPerPage: 50,
      visibleData: [],
      loadingData: false,
      nbrOfPages: 0,
      exporting: false,
      exportError: '',
      activeAbortController: null,
    };
    this.dialogRef = React.createRef<FilterDialogRaw>();
    this.updatePage = this.updatePage.bind(this);
    this.state.appliedFilters.push(this.createFilter('Version'));
  }

  private getData(): void {
    if (this.state.activeAbortController) {
      this.state.activeAbortController.abort();
    }

    const dataFilter = this.state.appliedFilters
      .filter((f) => f.value)
      .reduce(
        (acc, curr) => {
          const name = this.translateFilterName(curr.name);
          const value = curr.value.replace(/\*/g, '%');
          if (acc[name]) {
            acc[name].push(value);
          } else {
            acc[name] = [value];
          }
          return acc;
        },
        {} as { [x: string]: string[] }
      );

    if (!(Object.keys(dataFilter ?? {}).length > 1)) {
      this.setState({data: [], visibleData: [], currentPage: 1, nbrOfPages: 0, hiddenRows: 0});
      return;
    }

    const newAbortController = new AbortController();
    this.setState({
      loadingData: true,
      currentPage: 1,
      nbrOfPages: 0,
      hiddenRows: 0,
      activeAbortController: newAbortController,
    });

    DataService.getFilteredData(dataFilter, newAbortController).then((unfilteredData) => {
      const data = unfilteredData.filter((x) =>
        x.dataValues.some((value) => this.state.visibleEmissions.includes(value.emissionYear) && !!value.value)
      );
      const hiddenRows = unfilteredData.length - data.length;
      const indexOfLastPost = this.state.currentPage * this.state.dataPerPage;
      const indexOfFirstPost = indexOfLastPost - this.state.dataPerPage;
      const visibleData = data.slice(indexOfFirstPost, indexOfLastPost);
      const nbrOfPages = Math.ceil(data.length / this.state.dataPerPage);
      this.setState({
        data,
        hiddenRows,
        visibleData,
        nbrOfPages,
        currentPage: 1,
        loadingData: false,
      });
    });
  }

  public componentDidMount(): void {
    DimensionsService.getEmissionYears().then((emissionYears) => {
      const visibleEmissions = emissionYears.map((x) => x.name).filter((x) => this.state.visibleEmissions.includes(x));
      this.setState({ visibleEmissions });
    });
    getDimensionTypes().then((dimensionTypes) => this.setState({ dimensionTypes }));
  }

  private createFilter(name: FilterName, value = ''): Filter {
    const id = this.state.appliedFilters.reduce((max, current) => Math.max(max, current.id), 0) + 1;
    return { name: name, value: value, id };
  }

  private translateFilterName(name: string): string {
    const filterMap: Record<string, string> = {
      CRF: 'crfCode',
      NFR: 'nfrCode',
      Undergrupp: 'subGroup',
    };

    return filterMap[name] || name.toLocaleLowerCase();
  }

  // change page
  private updatePage(newPage: number) {
    const indexOfLastPost = newPage * this.state.dataPerPage;
    const indexOfFirstPost = indexOfLastPost - this.state.dataPerPage;
    const visibleData = this.state.data.slice(indexOfFirstPost, indexOfLastPost);
    this.setState({ visibleData, currentPage: newPage });
  }

  private handleExport(appliedFilters: Filter[]): void {
    this.setState({ exporting: true, exportError: '' });
    DataService.getDataExport(appliedFilters)
      .then(() => this.setState({ exporting: false }))
      .catch((err) => {
        this.setState({ exporting: false, exportError: err.message });
      });
  }

  public render(): React.ReactNode {
    const { loadingData, visibleData, visibleDimensions, visibleEmissions } = this.state;

    const updateFilters = (visibleDimensions: FilterName[], visibleEmissions: string[]) => {
      this.setState({ visibleDimensions, visibleEmissions });
      localStorage.setItem(ViewPage.FILTER_DIMENSION_COLUMNS_KEY, JSON.stringify(visibleDimensions));
      localStorage.setItem(ViewPage.FILTER_EMISSION_COLUMNS_KEY, JSON.stringify(visibleEmissions));
      this.getData();
    };

    return (
      <Box sx={{ flexGrow: 1, maxWidth: '100vw', padding: 4 }}>
        <Grid flexDirection={'column'} spacing={2}>
          <ViewPageHeader
            dataLength={this.state.data.length}
            exporting={this.state.exporting}
            handleExport={() => this.handleExport(this.state.appliedFilters)}
            exportError={this.state.exportError}
            dialogRef={this.dialogRef}
            visibleEmissions={this.state.visibleEmissions}
            visibleDimensions={this.state.visibleDimensions}
          />

          <FilterSection
            filters={this.state.appliedFilters}
            timeDimensionId={this.state.timeDimensionId}
            dimensionTypes={this.state.dimensionTypes}
            setAppliedFilters={(filters) => this.setState({ appliedFilters: filters }, () => this.getData())}
            setTimeDimensionId={(id) => this.setState({ timeDimensionId: id })}
          />

          <ShowTableOrLoad
            loadingData={loadingData}
            visibleData={visibleData}
            visibleDimensions={visibleDimensions}
            visibleEmissions={visibleEmissions}
          />

          <ViewPagePagination
            nbrOfPages={this.state.nbrOfPages}
            currentPage={this.state.currentPage}
            handlePageChange={this.updatePage}
          />

          <Typography variant="body1">Det finns {this.state.hiddenRows} rader som göms av ditt filter.</Typography>
          <FilterDialog ref={this.dialogRef} onDone={updateFilters} />
        </Grid>
      </Box>
    );
  }
}

export default ViewPage;
